import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useDispatch, useStore } from "react-redux";
import { Box, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";

import {
  setAllGroupUser,
  setGroupUser,
} from "../../../../redux/TaskGroupingSlice";
import Status from "./Status";
import GlobalSearch from "../../../../components/Search";
import { addAllBoard, searchCard } from "../../../../redux/SearchSlice";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { useBoards } from "../../../../utils/reactQuery/boards";
import { setActiveBoards } from "../../../../redux/ActiveBoardSlice";
import { useResponsive } from "../../../../hooks/useResponsive";
import Team from "../../components/Filters/Team";
import Boards from "../../components/Filters/Boards";
import ToggleInput from "../../../../components/ToggleInput";
import DrawerGlobalComp from "../../../../components/Drawer";
import { useUsers } from "../../../../utils/reactQuery/users";
import MobileFilterButton from "./MobileFilterButton";
import ReplayIcon from "@mui/icons-material/Replay";
import SelectDatePicker from "../../components/Filters/SelectDatePicker";
import { CustomButton } from "../../../../components/Button/Button";
import { useSetMultipleParams } from "../../../../hooks/useSetMultipleParams";

const Header = React.memo(
  ({ teams, teamIsLoading, groupingUserFromRedux }) => {
    const dispatch = useDispatch();
    const [filteredUser, setFilteredUser] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchParam, setSearchParam] = useCustomSearchParams("txt");
    const isMobile = useResponsive("down", "md");
    const { boards } = useBoards();
    const oldBoard = useMemo(() => searchParams.get("board"), [searchParams]);
    const oldDate = useMemo(() => searchParams.get("date"), [searchParams]);

    const [filterDrawer, setFilterDrawer] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [userParams, setUserParams] = useCustomSearchParams("uid");
    const { users, isLoading: usersIsLoading } = useUsers();
    const [openSearchField, setOpenSearchField] = useState(false);
    const store = useStore();
    const [setMultipleSearchParams] = useSetMultipleParams();

    useEffect(() => {
      dispatch(
        setActiveBoards(
          boards?.data?.data?.responseData
            .filter((el) => !el.is_archive)
            .sort(function (a, b) {
              return a.board_name.localeCompare(b.board_name);
            }) || []
        )
      );
    }, [boards]);

    useEffect(() => {
      if (teams && users) {
        if (userParams && userParams[0]) {
          userParams?.split(",").forEach((element) => {
            dispatch(
              setGroupUser(
                users.data.data.responseData.find(
                  (user) => user.user_id === Number(element)
                )
              )
            );
          });
        }
      }
    }, [teamIsLoading, usersIsLoading]);

    useEffect(() => {
      setInputValue(searchParam || "");
      dispatch(searchCard(searchParam || ""));
      if (
        searchParams.get("board") ||
        searchParams.get("uid") ||
        searchParams.get("switch") === "true"
      ) {
        setIsVisible(true);
      }

      return () => {
        clearFilters();
      };
    }, []);

    const removeSearchParams = (paramsToRemove) => {
      const newSearchParams = new URLSearchParams(searchParams);

      paramsToRemove.forEach((param) => {
        newSearchParams.delete(param);
      });

      setSearchParams(newSearchParams);
    };
    const clearFilters = useCallback(() => {
      dispatch(setAllGroupUser([]));
      dispatch(addAllBoard([]));
      if (searchParams.get("board")) {
        searchParams.delete("board")
      }
      if (searchParams.get("uid")) {
        searchParams.delete("uid")
      }
      setInputValue("");
    }, [dispatch]);

    const debouncedSearch = useRef(
      _.debounce((value) => {
        dispatch(searchCard(value));
        setSearchParam("txt", value);
      }, 300)
    ).current;

    const handleChange = (event) => {
      const value = event.target.value.trimStart();
      setInputValue(value);
      debouncedSearch(value);
    };

    useEffect(() => {
      setFilteredUser(
        groupingUserFromRedux
          .map((item) => item.users)
          .flat()
          .filter((el) => el.active)
      );
    }, [groupingUserFromRedux]);

    const handleOpenSearchField = useCallback(() => {
      setOpenSearchField(!openSearchField);
    }, [openSearchField]);

    const handleApplyFilter = () => {
      const currentState = store.getState();
      const currentBoardSearch = currentState.search.boardSearch;
      const currentGroupingUser = currentState.groupingUser.allocationUsers;
      const updatedSearchParams = [];

      updatedSearchParams.push({
        name: "board",
        value: currentBoardSearch.join(","),
      });
      updatedSearchParams.push({
        name: "uid",
        value: (currentGroupingUser[0]?.users || [])
          .map((item) => item.user_id)
          .join(","),
      });
      setMultipleSearchParams(updatedSearchParams);
      setFilterDrawer(false);
    };
    return (
      <div>
        {!isMobile && (
          <div className="d-flex">
            <p className="board-heading-name">Task Assignments</p>
            <Status setIsVisible={() => setIsVisible(true)} />
          </div>
        )}
        {isMobile ? (
          openSearchField ? (
            <Grid
              container
              item
              xs={12}
              className="remove-container-width fixed-ui"
              justifyContent={"center"}
              alignItems={"center"}>
              <GlobalSearch
                placeholder={"Search..."}
                searchText={inputValue}
                handleChange={handleChange}
                onCancel={handleOpenSearchField}
                showCancel={true}
                fullWidth={true}
              />
            </Grid>
          ) : (
            <MobileFilterButton
              setFilterDrawer={setFilterDrawer}
              handleOpenSearchField={handleOpenSearchField}
            />
          )
        ) : (
          <div className="d-flex align-items-center mt-4">
            <ToggleInput
              value={isVisible}
              setValue={(value) => {
                setIsVisible(value);
                if (!value) {
                  clearFilters();
                }
              }}
            />
            <div className={`slide-panel d-flex ${isVisible ? "open" : ""}`}>
              <Team team={teams} />

              <Boards />
              <div className="ml-4">
                <GlobalSearch
                  placeholder={"Search..."}
                  searchText={inputValue}
                  handleChange={handleChange}
                />
              </div>
            </div>
          </div>
        )}
        {filterDrawer && (
          <DrawerGlobalComp open={filterDrawer} setOpen={setFilterDrawer}>
            <Box>
              <div className="filter-head-container">
                <p className="filter-head-mobile">Filter By</p>
                {(oldBoard || filteredUser.length > 0 || oldDate) && (
                  <div
                    className="d-flex align-items-center"
                    onClick={clearFilters}>
                    <ReplayIcon className="reset-mobile-icon" />
                    <p className="reset-text-mobile">Reset</p>
                  </div>
                )}
              </div>
              <div className="filter-body-container">
                {/* <p className="filter-text">By Date:</p>
                <div
                  style={{
                    maxHeight: "180px",
                    overflow: "auto",
                    marginTop: "8px",
                  }}>
                  <SelectDatePicker />
                </div> */}

                <div className="mt-4">
                  <Team team={teams} />
                </div>

                <div className="mt-4">
                  <Boards />
                </div>
              </div>

              <CustomButton
                className=" capitalize mobile-board-filter-apply-btn"
                variant="outlined"
                onClick={handleApplyFilter}>
                Apply
              </CustomButton>
            </Box>
          </DrawerGlobalComp>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    const teamsChanged =
      JSON.stringify(prevProps.teams) !== JSON.stringify(nextProps.teams);
    const teamLoadingChanged =
      prevProps.teamIsLoading !== nextProps.teamIsLoading;
    const groupingUserChanged =
      JSON.stringify(prevProps.groupingUserFromRedux) !==
      JSON.stringify(nextProps.groupingUserFromRedux);

    return !teamsChanged && !teamLoadingChanged && !groupingUserChanged;
  }
);

export default Header;
